import React, { useState, useRef, useMemo } from "react";
import {
  TextField,
  Grid,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Pagination,
  Stack,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { format } from "date-fns";
import { Notification } from "../../utils/Notification";
import ScaleLoader from "react-spinners/ScaleLoader";
import useStyles from "../../assets/styles/SupMangComp.style";
import { useNavigate, useLocation } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import noTableData from "../../assets/images/noTableData.svg";
import API from "../../utils/Axios.utils";
import CustomModel from "../../utils/CustomModel";
import JobDetailsDialog from "./JobDetailsDialog";
import SupplierDetails from "../SupplierManagement/SupplierDetails";
import Checkbox from "@mui/material/Checkbox";
import AssignSupplier from "./AssignSupplier"; // adjust path
import AssignRecruiter from "./AssignRecruiter";

import useJobManagement from "../../hooks/useJobManagement";
import useAuth from "../../hooks/useAuth";

const JobMangComp = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedJobs, setSelectedJobs] = useState([]); // store job_ids
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignRecruiterOpen, setAssignRecruiterOpen] = useState(false);
  const [multipleDeleteOpen, setMultipleDeleteOpen] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const [fromDate, setFromDate] = useState(queryParams.get("fromDate") || "");
  const [toDate, setToDate] = useState(queryParams.get("toDate") || "");
  const [jobStatus, setJobStatus] = useState(queryParams.get("status") || "");
  const { token } = useAuth();

  const {
    page,
    listCount,
    handlePageChange,
    searchText,
    setSearchText,
    showLoader,
    setShowLoader,
    jobData,
    alert,
    alertContent,
    alertType,
    setAlert,
    setAlertContent,
    setAlertType,
    handleAlertClose,
    forceUpdate,
  } = useJobManagement({
    fetchUrlBase: "/api/job/alljob",
    dataKey: "jobPost",
    extraParams: { fromDate, toDate, status: jobStatus },
    resetOnParamsChange: true,
  });

  // Only the job owner may assign a recruiter. can_assign is computed by the
  // backend per row — the frontend cannot tell Intelliconvo ids from TEP ids,
  // and ownership is a permission, so it is not decided here.
  // Rows selected on a previous page are no longer in jobData; those are left
  // to the backend, which enforces ownership and reports what it skipped.
  const canAssignSelected =
    selectedJobs.length > 0 &&
    selectedJobs.every((id) => {
      const job = (jobData || []).find((j) => j?.job_id === id);
      return !job || job.can_assign;
    });

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    const newParams = new URLSearchParams(location.search);
    if (value) newParams.set("search", value);
    else newParams.delete("search");
    newParams.set("page", "1");
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const handleClearSearch = () => {
    setSearchText("");
    const newParams = new URLSearchParams(location.search);
    newParams.delete("search");
    newParams.set("page", "1");
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const newParams = new URLSearchParams(location.search);

    if (name === "fromDate") {
      setFromDate(value);
      if (toDate && value > toDate) {
        setAlertContent("To Date cannot be earlier than From Date.");
        setAlertType("error");
        setAlert(true);
        return;
      }
      if (value) newParams.set("fromDate", value);
      else newParams.delete("fromDate");
    } else {
      setToDate(value);
      if (fromDate && value < fromDate) {
        setAlertContent("To Date cannot be earlier than From Date.");
        setAlertType("error");
        setAlert(true);
        return;
      }
      if (value) newParams.set("toDate", value);
      else newParams.delete("toDate");
    }

    newParams.set("page", "1");
    navigate(`?${newParams.toString()}`, { replace: true });
  };
  const handleStatusChange = (e) => {
    const value = e.target.value;
    setJobStatus(value);
    const newParams = new URLSearchParams(location.search);
    if (value) newParams.set("status", value);
    else newParams.delete("status");
    newParams.set("page", "1");
    navigate(`?${newParams.toString()}`, { replace: true });
  };

  const handleNavigate = () => {
    navigate("/jobs/addJob");
  };



  const handleDownloadReport = async () => {
    try {
      setShowLoader(true);
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        params: {
          fromDate,
          toDate,
          search: searchText,
          status: jobStatus,
          selectedJobIds: selectedJobs.length > 0 ? selectedJobs.join(',') : undefined
        }
      };

      const response = await API.get('/api/job/job-report', config);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `JobWise_Report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowLoader(false);
    } catch (error) {
      setShowLoader(false);
      setAlertContent(error?.response?.data?.message || 'Failed to download report');
      setAlertType('error');
      setAlert(true);
    }
  };

  const parseStatusLabel = (status) => {
    if (!status) return "";
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleMultipleJobDelete = () => {
    API.delete(`/api/job/delete-multiple`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { jobIds: selectedJobs },
    })
      .then((res) => {
        setMultipleDeleteOpen(false);
        setSelectedJobs([]);
        forceUpdate();
        setAlertContent(res?.data?.message || "Jobs deleted successfully");
        setAlertType("success");
        setAlert(true);
      })
      .catch((error) => {
        setAlertContent(error?.response?.data?.message || "Failed to delete jobs");
        setAlertType("error");
        setAlert(true);
      });
  };

  return (
    <div style={{ marginTop: "20px" }}>
      <Grid
        container
        xs={12}
        style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
      >
        <Button
          variant="contained"
          disabled={selectedJobs.length === 0}
          onClick={() => setMultipleDeleteOpen(true)}
          sx={{
            width: "214px",
            height: "40px",
            textTransform: "none",
            backgroundColor: "#d32f2f",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#9a0007",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e0e0e0",
              color: "#757575",
            }
          }}
        >
          Delete Selected
        </Button>
        <Button
          variant="contained"
          disabled={selectedJobs.length === 0}
          onClick={() => {
            const allowedStatuses = ['Active', 'Partially-Fulfilled', 'Critical'];
            const nonActiveJobs = jobData.filter(j => selectedJobs.includes(j.job_id) && !allowedStatuses.includes(j.job_status));

            if (nonActiveJobs.length > 0) {
              setAlertContent(`Note: ${nonActiveJobs.length} selected job(s) are not Active/Critical and will be excluded from email notification.`);
              setAlertType("warning");
              setAlert(true);
            }
            setAssignOpen(true);
          }}
          sx={{
            width: "214px",
            height: "40px",
            textTransform: "none",
            backgroundColor: "#2777AA",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#1e5c84",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e0e0e0",
              color: "#757575",
            }
          }}
        >
          Assign to Supplier
        </Button>

        <Button
          variant="contained"
          disabled={selectedJobs.length === 0 || !canAssignSelected}
          title={
            selectedJobs.length > 0 && !canAssignSelected
              ? "You can only assign recruiters to jobs you own"
              : ""
          }
          onClick={() => setAssignRecruiterOpen(true)}
          sx={{
            width: "214px",
            height: "40px",
            textTransform: "none",
            backgroundColor: "#2777AA",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#1e5c84",
            },
            "&.Mui-disabled": {
              backgroundColor: "#e0e0e0",
              color: "#757575",
            }
          }}
        >
          Assign Recruiter
        </Button>

        <Button
          variant="contained"
          aria-haspopup="true"
          onClick={handleNavigate}
          style={{ background: "#2777AA", width: "214px" }}
        >
          + Add Job
        </Button>
      </Grid>
      <Grid container xs={12} style={{ marginTop: "2%" }}>
        <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <span className={classes.headingText}>Job</span>
        </Grid>
        <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
          <TextField
            name="fromDate"
            label="From Updated Date"
            type="date"
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '180px' }}
            value={fromDate}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            name="toDate"
            label="To Updated Date"
            type="date"
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '180px' }}
            value={toDate}
            onChange={handleDateChange}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            name="search"
            label="Search by Job Name or Client Name"
            autoComplete="off"
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '230px' }}
            value={searchText}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />

          <TextField
            select
            label="Status"
            value={jobStatus}
            onChange={handleStatusChange}
            variant="outlined"
            size="small"
            style={{ backgroundColor: '#fff', width: '150px' }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Partially-Fulfilled">Partially-Fulfilled</MenuItem>
            <MenuItem value="Full-Filled">Full-Filled</MenuItem>
            <MenuItem value="Critical">Critical</MenuItem>
            <MenuItem value="Hold">Hold</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
            <MenuItem value="Close-No_Client_Response">Close-No Client Response</MenuItem>
            <MenuItem value="Close-Unable_To_Fulfill">Close-Unable To Fulfill</MenuItem>
          </TextField>

          <Button
            type="button"
            variant="contained"
            color="success"
            onClick={handleDownloadReport}
            // style={{
            //   height: '40px',
            //   padding: '0 16px',
            //   textTransform: 'none',
            //   display: 'inline-flex',
            //   alignItems: 'center',
            //   justifyContent: 'center',
            // }}
            sx={{
              px: 2,      // horizontal padding
              py: 0.1,      // vertical padding
              textTransform: 'none',
              fontSize: 14,
              borderRadius: 2,
            }}
          >
            Download Report
          </Button>
        </Grid>
      </Grid>

      <Grid container xs={12} className={classes.tableGrid}>
        {showLoader ? (
          <div
            style={{
              display: "flex",
              height: 400,
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <ScaleLoader size={250} color={"#0576DC"} loading={showLoader} />
          </div>
        ) : jobData.length === 0 ? (
          <div
            style={{
              height: 300,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <img src={noTableData} width={60} alt="No Data" />
            <div
              style={{
                marginTop: 5,
                fontWeight: "500",
                fontSize: 25,
                fontFamily: "'Noto Sans', sans-serif",
              }}
            >
              There are no recent Jobs to show.
            </div>
          </div>
        ) : (
          <TableContainer component={Paper} elevation={0} style={{ boxShadow: 'none' }}>
            <Table
              sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}
              size="small"
              aria-label="a dense table"
            >
              <TableHead>
                <TableRow className={classes.tableHeadRow}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        '&.Mui-checked': {
                          color: '#fff',
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: '#fff',
                        },
                      }}
                      checked={selectedJobs.length === jobData.length && jobData.length > 0}
                      indeterminate={
                        selectedJobs.length > 0 &&
                        selectedJobs.length < jobData.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedJobs(jobData.map(j => j.job_id));
                        } else {
                          setSelectedJobs([]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Job Code
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Job Title
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Client
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Job Owner
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Recruiter
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Core Skill
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Created Date
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Updated Date
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Job Age (Days)
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Status
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.noHorizontalBorderTableRow}>
                {(jobData || []).filter(item => item != null).map((val) => {
                  return (
                    <TableRow key={val.job_id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          sx={{
                            color: '#2777AA',
                            '&.Mui-checked': {
                              color: '#2777AA',
                            },
                          }}
                          checked={selectedJobs.includes(val.job_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedJobs(prev => [...prev, val.job_id]);
                            } else {
                              setSelectedJobs(prev =>
                                prev.filter(id => id !== val.job_id)
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val.job_code}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val.title}
                        {val.needs_to_review && (
                          <span
                            style={{ marginLeft: "8px", verticalAlign: "middle", fontSize: "16px" }}
                            title="Automated Job - Needs Review"
                          >
                            ⚠️
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val?.client?.name || ''}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val?.owner_name || '-'}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {/* Falls back to the owner server-side when unassigned */}
                        {val?.recruiter_name || '-'}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val?.jobCoreSkill?.name || ''}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val.createdAt ? format(new Date(val.createdAt), "dd-MM-yyyy") : '-'}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val.updatedAt ? format(new Date(val.updatedAt), "dd-MM-yyyy") : '-'}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {val?.agingInDays || 0}
                      </TableCell>
                      <TableCell className={classes.tableBodyCell}>
                        {/* {val.job_status} */}
                        {parseStatusLabel(val.job_status) || '-'}
                      </TableCell>
                      <TableCell style={{ display: 'flex', alignItems: 'center' }}>

                        <JobMoreMenu
                          details={val}
                          navigate={navigate}
                          forceUpdate={forceUpdate}
                          setAlertContent={setAlertContent}
                          setAlertType={setAlertType}
                          setAlert={setAlert}
                          onJobDeleted={() => {
                            setSelectedJobs((prev) =>
                              prev.filter((id) => id !== val.job_id)
                            );
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Stack
              spacing={2}
              sx={{
                padding: 2,
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
              }}
            >
              <Pagination
                classes={{
                  root: classes.pagination,
                }}
                count={Math.ceil(listCount / 10)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
              />
            </Stack>
          </TableContainer>
        )}
      </Grid>


      <CustomModel
        open={assignOpen}
        handleDialog={() => setAssignOpen(false)}
        title="Assign Supplier"
        width="sm"
      >
        <AssignSupplier
          jobIds={selectedJobs}     // ✅ ARRAY of job IDs
          onSuccess={() => {
            setAssignOpen(false);
            setSelectedJobs([]);
          }}
          setAlert={setAlert}
          setAlertType={setAlertType}
          setAlertContent={setAlertContent}
        />
      </CustomModel>

      <CustomModel
        open={assignRecruiterOpen}
        handleDialog={() => setAssignRecruiterOpen(false)}
        title="Assign Recruiter"
        width="sm"
      >
        <AssignRecruiter
          jobIds={selectedJobs}
          onSuccess={() => {
            setAssignRecruiterOpen(false);
            setSelectedJobs([]);
            forceUpdate();
          }}
          setAlert={setAlert}
          setAlertType={setAlertType}
          setAlertContent={setAlertContent}
        />
      </CustomModel>

      <CustomModel
        handleDialog={() => setMultipleDeleteOpen(!multipleDeleteOpen)}
        open={multipleDeleteOpen}
        title="Delete Jobs"
      >
        <div
          style={{
            textAlign: "center",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "24px",
          }}
        >
          Do you want to delete {selectedJobs.length} selected job(s)?
        </div>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1em",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={handleMultipleJobDelete}
              style={{ background: "#2777AA" }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              onClick={() => setMultipleDeleteOpen(false)}
              style={{ background: "#2777AA" }}
            >
              NO
            </Button>
          </div>
        </Grid>
      </CustomModel>

      <Notification
        message={alertContent}
        open={alert}
        severity={alertType}
        onClose={handleAlertClose}
      />
    </div>
  );
};

export default JobMangComp;

function JobMoreMenu({
  details,
  navigate,
  forceUpdate,
  setAlertContent,
  setAlertType,
  setAlert,
  onJobDeleted,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [delOpen, setDelOpen] = React.useState(false);
  const [mailOpen, setMailOpen] = React.useState(false);
  // Holds the freshly-fetched job details for the View dialog. Starts as
  // `details` (the row data already in memory) so the dialog opens
  // instantly, then gets replaced once GET /api/job/code/:jobCode resolves.
  const [viewDetails, setViewDetails] = React.useState(details);
  const location = useLocation();
  const { token } = useAuth();

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleView = () => {
    setOpen(true);
    handleMenuClose();
    // Show the row's cached data immediately, then hit the API for the
    // authoritative copy (same endpoint the /jobs/code/:jobCode deep link
    // uses) and swap it in once it lands.
    setViewDetails(details);
    API.get(`/api/job/code/${encodeURIComponent(details.job_code)}`, config)
      .then((res) => {
        setViewDetails(res.data);
      })
      .catch((error) => {
        setAlertContent(
          error?.response?.data?.message || "Failed to load latest job description."
        );
        setAlertType("error");
        setAlert(true);
      });
  };

  const handleEdit = () => {
    navigate("/jobs/editJob", { state: { jobDetails: details, from: location.search } });
    handleMenuClose();
  };

  const handleSendEmail = () => {
    setMailOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setDelOpen(true);
    handleMenuClose();
  };

  const handleJobDelete = () => {
    API.delete(`/api/job/${details.job_id}`, config)
      .then((res) => {
        handleDialogdel();
        forceUpdate();
        if (onJobDeleted) onJobDeleted();
        setAlertContent(res?.data?.message);
        setAlertType("success");
        setAlert(true);
      })
      .catch((error) => {
        setAlertContent(error?.response?.data?.message);
        setAlertType("error");
        setAlert(true);
      });
  };

  const handleDialog = () => {
    setOpen(!open);
  };

  const handleDialogMail = () => {
    setMailOpen(!mailOpen);
  };

  const handleDialogdel = () => {
    setDelOpen(!delOpen);
  };

  return (
    <>
      <IconButton
        onClick={handleMenuClick}
        size="small"
        sx={{ color: "#2777AA" }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <RemoveRedEyeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View" />
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={handleDelete} >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      <CustomModel
        handleDialog={handleDialog}
        open={open}
        title="Job Description"
      >
        <JobDetailsDialog details={viewDetails} />
      </CustomModel>

      <CustomModel
        handleDialog={handleDialogMail}
        open={mailOpen}
        title="Send Mail to supplier"
        width="lg"
      >
        <SupplierDetails
          JobId={details.job_id}
          handleDialog={handleDialogMail}
          setAlertContent={setAlertContent}
          setAlertType={setAlertType}
          setAlert={setAlert}
        />
      </CustomModel>

      <CustomModel
        handleDialog={handleDialogdel}
        open={delOpen}
        title="Delete Job"
      >
        <div
          style={{
            textAlign: "center",
            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontSize: "24px",
          }}
        >
          Do you want to delete the Job?
        </div>
        <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1em",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={handleJobDelete}
              style={{ background: "#2777AA" }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              onClick={handleDialogdel}
              style={{ background: "#2777AA" }}
            >
              NO
            </Button>
          </div>
        </Grid>
      </CustomModel>
    </>
  );
}
